'use client';

/**
 * PropertyImagePicker
 *
 * Replaces the file-upload ImageUploader in portal mode.
 * Shows the user's wishlisted properties and lets them pick
 * an existing property image to reimagine with AI.
 */

import { cn } from '@/lib/utils';
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  Heart,
  ImageIcon,
  Loader2,
  MapPin,
} from 'lucide-react';
import Image from 'next/image';
import React, { useCallback, useState } from 'react';
import type { PropertyImage, WishlistPropertyImage } from './types';

interface PropertyImagePickerProps {
  wishlistProperties: WishlistPropertyImage[];
  currentImage: string | null;
  onImageSelected: (base64: string | null) => void;
  isLoading?: boolean;
}

async function fetchImageViaProxy(imageUrl: string): Promise<string> {
  const response = await fetch('/api/spaces/image-proxy', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url: imageUrl }),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || 'Failed to fetch image');
  }

  const { dataUrl } = (await response.json()) as { dataUrl: string };
  return dataUrl;
}

export const PropertyImagePicker: React.FC<PropertyImagePickerProps> = ({
  wishlistProperties,
  currentImage,
  onImageSelected,
  isLoading = false,
}) => {
  const [selectedProperty, setSelectedProperty] =
    useState<WishlistPropertyImage | null>(null);
  const [loadingImageId, setLoadingImageId] = useState<string | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);

  const handleImageSelect = useCallback(
    async (image: PropertyImage) => {
      setLoadingImageId(image.id);
      setImageError(null);
      try {
        const base64 = await fetchImageViaProxy(image.url);
        onImageSelected(base64);
      } catch (err) {
        console.error('[PropertyImagePicker] Failed to load image:', err);
        setImageError(
          'Could not load this image. Please try another photo or try again later.'
        );
      } finally {
        setLoadingImageId(null);
      }
    },
    [onImageSelected]
  );

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-stone-400 mb-4" />
        <p className="text-stone-400 text-sm">
          Loading your saved properties...
        </p>
      </div>
    );
  }

  // If an image is already selected, show the preview
  if (currentImage) {
    return (
      <div className="relative w-full rounded-xl overflow-hidden border-2 border-gold-500/50 group bg-black">
        {/* eslint-disable-next-line @next/next/no-img-element -- base64 data URL */}
        <img
          src={currentImage}
          alt="Selected room"
          className="w-full h-auto max-h-[70vh] object-contain mx-auto"
        />
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <button
            onClick={() => {
              onImageSelected(null);
              setSelectedProperty(null);
            }}
            className="bg-red-500/80 hover:bg-red-600 text-white px-6 py-3 rounded-full flex items-center gap-2 transition-transform transform hover:scale-105"
          >
            <ArrowLeft size={20} /> Choose Different Image
          </button>
        </div>
      </div>
    );
  }

  // If no wishlisted properties, show empty state
  if (wishlistProperties.length === 0) {
    return (
      <div className="text-center py-16 bg-stone-900/50 rounded-xl border border-dashed border-stone-700">
        <div className="w-16 h-16 bg-stone-800 rounded-full flex items-center justify-center mx-auto mb-4">
          <Heart size={24} className="text-stone-500" />
        </div>
        <h3 className="text-lg font-medium text-white mb-2">
          No saved properties
        </h3>
        <p className="text-stone-400 text-sm max-w-md mx-auto">
          You need to save properties to your wishlist before you can redesign
          their rooms. Browse properties and tap the heart icon to save them.
        </p>
      </div>
    );
  }

  // If a property is selected, show its images
  if (selectedProperty) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSelectedProperty(null)}
            className="p-2 rounded-full hover:bg-stone-800 text-stone-400 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h3 className="text-lg font-medium text-white">
              {selectedProperty.propertyName}
            </h3>
            <p className="text-stone-400 text-sm flex items-center gap-1">
              <MapPin size={12} />
              {selectedProperty.propertyLocation}
            </p>
          </div>
        </div>

        {imageError && (
          <div className="flex items-center gap-3 bg-red-900/40 border border-red-700/50 text-red-200 px-4 py-3 rounded-xl text-sm">
            <AlertTriangle size={16} className="shrink-0 text-red-400" />
            {imageError}
          </div>
        )}

        {selectedProperty.images.length === 0 ? (
          <div className="text-center py-12 bg-stone-900/50 rounded-xl border border-dashed border-stone-700">
            <ImageIcon size={32} className="text-stone-500 mx-auto mb-3" />
            <p className="text-stone-400 text-sm">
              No images available for this property.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {selectedProperty.images.map((image) => (
              <button
                key={image.id}
                onClick={() => handleImageSelect(image)}
                disabled={loadingImageId !== null}
                className={cn(
                  'relative aspect-4/3 rounded-xl overflow-hidden border-2 transition-all duration-200 group/img',
                  'hover:border-gold-500/50 hover:shadow-[0_0_20px_rgba(204,165,41,0.15)]',
                  loadingImageId === image.id
                    ? 'border-gold-500 opacity-80'
                    : 'border-stone-700'
                )}
              >
                <Image
                  src={image.thumbnailUrl || image.url}
                  alt={image.alt || 'Property photo'}
                  fill
                  className="object-cover group-hover/img:scale-105 transition-transform duration-500"
                  unoptimized
                />
                {loadingImageId === image.id ? (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-gold-500" />
                  </div>
                ) : (
                  <div className="absolute inset-0 bg-black/0 group-hover/img:bg-black/30 transition-colors flex items-center justify-center">
                    <div className="opacity-0 group-hover/img:opacity-100 transition-opacity bg-white/10 backdrop-blur-sm border border-white/20 rounded-full p-3">
                      <Check size={20} className="text-white" />
                    </div>
                  </div>
                )}
                {image.usageContext && (
                  <div className="absolute top-2 left-2 bg-black/60 text-white/80 px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider backdrop-blur-sm">
                    {image.usageContext === 'PRIMARY_HERO'
                      ? 'Hero'
                      : image.usageContext
                          .replace(/_/g, ' ')
                          .toLowerCase()
                          .replace(/^\w/, (c) => c.toUpperCase())}
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Default: show property list
  return (
    <div className="space-y-4">
      <p className="text-stone-400 text-sm">
        Select a property from your saved collection, then choose a room photo
        to reimagine.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {wishlistProperties.map((property) => {
          const hasImages = property.images.length > 0;
          return (
            <button
              key={property.propertyId}
              onClick={() => hasImages && setSelectedProperty(property)}
              disabled={!hasImages}
              className={cn(
                'group text-left bg-stone-900 border rounded-xl overflow-hidden transition-all duration-200',
                hasImages
                  ? 'border-stone-700 hover:border-stone-500 hover:shadow-lg cursor-pointer'
                  : 'border-stone-800 opacity-50 cursor-not-allowed'
              )}
            >
              <div className="relative aspect-video">
                {property.images[0] ? (
                  <Image
                    src={
                      property.images[0].thumbnailUrl || property.images[0].url
                    }
                    alt={property.propertyName}
                    fill
                    className="object-cover group-hover:scale-105 transition-transform duration-500"
                    unoptimized
                  />
                ) : (
                  <div className="w-full h-full bg-stone-800 flex items-center justify-center">
                    <ImageIcon size={32} className="text-stone-600" />
                  </div>
                )}
                <div className="absolute bottom-2 right-2 bg-black/60 text-white/80 px-2 py-0.5 rounded text-xs backdrop-blur-sm">
                  {hasImages
                    ? `${property.images.length} ${property.images.length === 1 ? 'photo' : 'photos'}`
                    : 'No photos available'}
                </div>
              </div>
              <div className="p-4">
                <h4 className="text-white font-medium truncate">
                  {property.propertyName}
                </h4>
                <p className="text-stone-400 text-sm flex items-center gap-1 mt-1 truncate">
                  <MapPin size={12} className="shrink-0" />
                  {property.propertyLocation}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
